import React, { useEffect } from 'react';
import { useStore } from '../../../../stores/root';
import PaymentMethods from '../../../../manifests/paymentMethods';
import moment from 'moment';

// assets
import { faArrowRight, faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../services/general/error';
import ValidationService from '../../../../services/validation';
import GeneralService from '../../../../services/general';
import OutcomeService from '../../../../services/finance/outcomes';
import OutcomePaymentService from '../../../../services/finance/outcomes/payments';

// props

// components
import { DatePicker, DefaultButton, Dropdown, IDropdownOption, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { IOutcomePaymentResourceProps } from '../../../../props/finance/outcomes/payments';
import SelectInvoice from '../../../uiframeworks/forms/selectInvoice';
import SelectBankAccount from '../../../uiframeworks/forms/bankaccounts';
import EmployeePayslipService from '../../../../services/employees/payslip';
import Text from '../../../typography/text';
import PermissionsService from '../../../../services/permissions';
import DepositService from '../../../../services/finance/deposits';
import { IVendorResourceShortProps } from '../../../../props/data/vendors';
import Details from '../../../typography/details';
import LoadingComponent from '../../../feedbacks/loading';
import { IEmployeeLeaveResourceProps } from '../../../../props/employees/leaves/leave';
import EmployeeLeaveService from '../../../../services/employees/leave';
import SelectEmployee from '../../../uiframeworks/forms/selectEmployee';
import SelectSubsidiary from '../../../uiframeworks/forms/selectSubsidiary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmployeeLeaveTypes from '../../../../manifests/types/employeeLeave';

interface EmployeeLeaveFormProps {
    leaveId?: string;
    onDismissed(refresh?: boolean, status?: string): void;
}

/*type FormDataProps = {
    method: string;
    amount: string;
    totalAmount: string;
    notes: string;
    tax: string;
    targetBankAccount?: string;
    targetVirtualAccountNumber: string;
    dueDate: string;
}

type FormDataErrorProps = {
    method?: string;
    amount?: string;
    ppn?: string;
    pph?: string;
    totalAmount?: string;
    notes?: string;
    tax?: string;
}*/

const EmployeeLeaveForm: React.FC<EmployeeLeaveFormProps> = (props: EmployeeLeaveFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<IEmployeeLeaveResourceProps>>({
        id: GeneralService.guid(),
        employee: user.data || undefined
    });
    const [error, setError] = React.useState<any>({});

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _data = data;

            if (props.leaveId) {
                const leave = await EmployeeLeaveService.read(props.leaveId);
                _data = leave;
            }

            setData({ ..._data });
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_invoices_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get invoices. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (!data.subsidiary) {
            return true;
        } else if ((data.reason || "").trim() === "" || !data.startDate || !data.endDate || !data.type) {
            return true;
        } else if (error.subsidiary || error.reason) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            const confirmed = window.confirm("Are you sure you want to submit this leave?");

            if (confirmed) {
                setSubmitting(true);
                const { employee, subsidiary, startDate, endDate, totalDays, type, reason } = data;

                // create form data
                const fd = new FormData();
                fd.append('employeeId', employee?.id || "");
                fd.append('subsidiaryId', subsidiary?.id || "");
                fd.append('startDate', moment(startDate).toISOString());
                fd.append('endDate', moment(endDate).toISOString());
                fd.append('totalDays', moment(endDate).diff(moment(startDate)) + "");
                fd.append('type', type || "");
                fd.append('reason', reason || "");

                let leave = undefined;
                if (props.leaveId) {
                    leave = await EmployeeLeaveService.update(props.leaveId, fd);
                } else {
                    leave = await EmployeeLeaveService.create(fd);
                }

                banner.add({
                    key: 'request_employee_leave_success',
                    variant: 'success',
                    icon: faCheck,
                    text: `Your leave request has been submitted.`
                });
                props.onDismissed(true, 'requested');
            }
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={'Request Leave'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Request"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Requesting leave ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <SelectEmployee selected={data.employee} disabled
                        label={'Employee'}
                        onChange={() => { }} />
                    <SelectSubsidiary selected={data.subsidiary}
                        label={'Subsidiary'}
                        required
                        onChange={(value) => {
                            let _data = data;
                            _data.subsidiary = value;

                            const validation = ValidationService.combination(value, ['required'], {});
                            error.subsidiary = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                    <Stack className='divider'></Stack>
                    <Dropdown label={"Leave type"} 
                        options={EmployeeLeaveTypes}
                        selectedKey={data.type}
                        required
                        onChange={(evt: any, opt?: IDropdownOption) => {
                            let _data = data;
                            _data.type = opt?.key as string;
                            setData({ ..._data });
                        }} />
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <DatePicker label={"Start date"}
                            isRequired={true}
                            maxDate={data.endDate ? moment(data.endDate).toDate() : undefined}
                            value={data.startDate ? moment(data.startDate).toDate() : undefined}
                            onSelectDate={(date) => {
                                let _data = data;
                                _data.startDate = date ? moment(date).toISOString() : _data.startDate;
                                setData({ ..._data });
                            }}
                            formatDate={GeneralService.formatDate}
                            disabled={submitting}
                            styles={{root: {width: '50%'}}} />
                        <DatePicker label={"End date"}
                            isRequired={true}
                            minDate={data.startDate ? moment(data.startDate).toDate() : undefined}
                            value={data.endDate ? moment(data.endDate).toDate() : undefined}
                            onSelectDate={(date) => {
                                let _data = data;
                                _data.endDate = date ? moment(date).toISOString() : _data.endDate;
                                setData({ ..._data });
                            }}
                            formatDate={GeneralService.formatDate}
                            disabled={submitting}
                            styles={{root: {width: '50%'}}} />
                    </Stack>
                    <Stack className='divider'></Stack>
                    <TextField label={"Reason"}
                        value={data.reason}
                        multiline
                        rows={5}
                        autoAdjustHeight
                        resizable={false}
                        required
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.reason = value;

                            const validation = ValidationService.combination(value, ['required', 'limit'], {maxChars: 2000});
                            error.reason = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                </> : null
            }
        </Stack>
    </Panel>
};

export default EmployeeLeaveForm;
